import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TabView, TabPanel } from 'primereact/tabview';
import {
    loadModuleQuestionsAsync,
    loadResidenceCombinationsAsync,
    loadMedicalServicesUserAsync,
    loadMedicalServicesByStateAsync,
    loadTestQuestionsAsync,
    loadImportationsTimelineAsync,
} from './dashboardSlice';

import { ActiveUsersCard } from './components/ActiveUsersCard';
import { NewUsersTodayCard } from './components/NewUsersTodayCard';
import { ActiveStudentsCard } from './components/ActiveStudentsCard';
import { ChurnRiskCard } from './components/ChurnRiskCard';
import { SubscriptionsExpiringCard } from './components/SubscriptionsExpiringCard';
import { TodaySessionsCard } from './components/TodaySessionsCard';
import { ModuleQuestionsChart } from './components/ModuleQuestionsChart';
import { ResidenceCombinationsChart } from './components/ResidenceCombinationsChart';
import { MedicalServicesUserChart } from './components/MedicalServicesUserChart';
import { MedicalServicesByStateChart } from './components/MedicalServicesByStateChart';
import { TestQuestionsChart } from './components/TestQuestionsChart';
import { ImportationsTimelineChart } from './components/ImportationsTimelineChart';
import { DailyNewUsersChart } from './components/DailyNewUsersChart';
import { SessionsActivityChart } from './components/SessionsActivityChart';

// Cada aba só busca seus dados na primeira vez que é selecionada.
// (Desempenho por Módulo/Prova/Questão saíram daqui — agora são páginas
// próprias, dentro da seção "Desempenho" do sidebar.) Ordem tem que bater
// com a ordem das TabPanel abaixo: Atividade, Módulos e Provas, Serviços e
// Regiões, Residência, Importações.
//
// A aba Atividade não tem loader próprio — os dois gráficos dela
// (SessionsActivityChart, DailyNewUsersChart) leem os mesmos slices que os
// cards do topo (TodaySessionsCard, NewUsersTodayCard), sempre montados;
// disparar de novo aqui só duplicava a mesma requisição.
const TAB_LOADERS = [
    () => {},
    (dispatch) => {
        dispatch(loadModuleQuestionsAsync());
        dispatch(loadTestQuestionsAsync());
    },
    (dispatch) => {
        dispatch(loadMedicalServicesUserAsync({ activeOnly: true }));
        dispatch(loadMedicalServicesByStateAsync());
    },
    (dispatch) => {
        dispatch(loadResidenceCombinationsAsync(true));
    },
    (dispatch) => {
        dispatch(loadImportationsTimelineAsync());
    },
];

const DashboardView = () => {
    const dispatch = useDispatch();
    const [activeIndex, setActiveIndex] = useState(0);
    const loadedTabs = useRef(new Set());

    const loadTab = useCallback((index) => {
        if (loadedTabs.current.has(index)) return;
        loadedTabs.current.add(index);
        TAB_LOADERS[index]?.(dispatch);
    }, [dispatch]);

    useEffect(() => {
        loadTab(activeIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="grid crud-demo">
            {/* <div className="col-12">
                <h5 className="m-0 mb-3">Dashboard</h5>
            </div> */}
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <ActiveUsersCard />
            </div>
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <TodaySessionsCard />
            </div>
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <NewUsersTodayCard />
            </div>
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <ActiveStudentsCard />
            </div>
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <SubscriptionsExpiringCard />
            </div>
            <div className="col-12 sm:col-6 lg:col-4 xl:col-2">
                <ChurnRiskCard />
            </div>
            <div className="col-12">
                <div className="card">
                    <TabView
                        activeIndex={activeIndex}
                        onTabChange={(e) => {
                            setActiveIndex(e.index);
                            loadTab(e.index);
                        }}
                    >
                        <TabPanel header="Atividade">
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <SessionsActivityChart />
                                </div>
                                <div className="col-12 lg:col-6">
                                    <DailyNewUsersChart />
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel header="Módulos e Provas">
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <ModuleQuestionsChart />
                                </div>
                                <div className="col-12 lg:col-6">
                                    <TestQuestionsChart />
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel header="Serviços e Regiões">
                            <div className="grid">
                                <div className="col-12">
                                    <MedicalServicesByStateChart />
                                    <MedicalServicesUserChart />
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel header="Residência">
                            <div className="grid">
                                <div className="col-12">
                                    <ResidenceCombinationsChart />
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel header="Importações">
                            <div className="grid">
                                <div className="col-12">
                                    <ImportationsTimelineChart />
                                </div>
                            </div>
                        </TabPanel>
                    </TabView>
                </div>
            </div>
        </div>
    );
};

export default DashboardView;
